import {
  CheckOutlined,
  CloseOutlined,
  MailOutlined,
  MoreOutlined,
  PrinterOutlined,
  TruckOutlined,
} from '@ant-design/icons';
import {
  DeleteButtonProps,
  EditButtonProps,
  ListButtonProps,
  RefreshButton,
  RefreshButtonProps,
} from '@refinedev/antd';
import {
  Button,
  ButtonProps,
  DatePicker,
  Flex,
  Form,
  Input,
  message,
  Modal,
  Select,
  Tooltip,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { ReactNode, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import PopupMenu from '@/components/general/PopupMenu';
import PackageMessages from '@/components/logistic/PackageMessages/PackageMessages';
import ProtectedDeleteButton from '@/components/protected/ProtectedDeleteButton';
import ProtectedEditButton from '@/components/protected/ProtectedEditButton';
import { usePermissionContext } from '@/contexts/PermissionContext';
import { useUiContext } from '@/contexts/UiContext';
import { useUserContext } from '@/contexts/UserContext';
import { USER_WORK_AREA } from '@/model/General/UserWorkArea';
import { PACKAGE_STATES } from '@/model/Logistic/PackageStates';
import { Reception } from '@/model/Logistic/Reception/Reception';
import { RECEPTION_TYPE } from '@/model/Logistic/Reception/ReceptionType';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';

interface ReceptionShowHeaderButtonsProps {
  record: Reception;
  refineButtonsProps: {
    editButtonProps: EditButtonProps | undefined;
    deleteButtonProps: DeleteButtonProps | undefined;
    refreshButtonProps: RefreshButtonProps | undefined;
    listButtonProps: ListButtonProps | undefined;
  };
  refetch: () => void;
  blockChangeState: { id: string; message: string }[];
  canFinishReception: boolean;
  canMarkAsSendingReception: boolean;
}

const ReceptionShowHeaderButtons = (props: ReceptionShowHeaderButtonsProps) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissionContext();
  const { userWorkArea } = useUserContext();
  const { isMediumScreen } = useUiContext();
  const { receptionRepository } = useRepositoriesContext();
  const [modal, contextHolder] = Modal.useModal();
  const {
    record,
    refineButtonsProps,
    refetch,
    blockChangeState,
    canFinishReception,
    canMarkAsSendingReception,
  } = props;
  const { editButtonProps, deleteButtonProps, refreshButtonProps } = refineButtonsProps;
  const { id, status, tipo } = record;
  const disableStateButtons = useMemo(() => blockChangeState.length > 0, [blockChangeState]);
  const receptionDateRef = useRef<Dayjs | null>(null);
  const cancelationMotiveRef = useRef<string>('');
  const emailListRef = useRef<string>('');
  // Mapea el string persistido temporalmente en el modal a un listado editable de tags.
  const emailListToArray = (value?: string) =>
    (value ?? '')
      .split(',')
      .map(email => email.trim())
      .filter(Boolean);
  // Normaliza los tags cargados a un string separado por comas para reutilizarlo en el flujo actual.
  const emailListToString = (value?: string[]) =>
    (value ?? [])
      .map(email => email.trim())
      .filter(Boolean)
      .join(',');
  // Valida los emails ingresados sin forzar el campo cuando el usuario no necesita enviarlos.
  const hasInvalidEmails = (value?: string) =>
    emailListToArray(value).some(email => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
  const changeStateButtonsProps: ButtonProps[] = useMemo(() => {
    if (!record) return [];

    return status === PACKAGE_STATES.PENDIENTE
      ? [
          {
            icon: <TruckOutlined />,
            onClick: () => {
              modal.confirm({
                title: t('¿Estás seguro de que deseas marcar en viaje esta recepción?'),
                content: t('Esta acción no se puede deshacer'),
                okText: t('Sí, marcar en viaje'),
                okType: 'primary',
                cancelText: t('No'),
                onOk() {
                  void receptionRepository
                    .updateReceptionState(id, PACKAGE_STATES.EN_VIAJE)
                    .then(() => {
                      void refetch();
                    });
                },
              });
            },
            disabled: !canMarkAsSendingReception,
            children: t('Marcar en viaje'),
          },
        ]
      : status == PACKAGE_STATES.EN_VIAJE && userWorkArea == USER_WORK_AREA.LOGISTICS
        ? [
            {
              icon: <CheckOutlined />,
              onClick: () => {
                receptionDateRef.current = null;
                modal.confirm({
                  title: t('¿Estás seguro de que deseas marcar como recibida?'),
                  content: (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                      {t('Esta acción no se puede deshacer')}
                      <b>{t('Fecha de recepción')}: </b>
                      <DatePicker
                        format={'L'}
                        placeholder={t('Seleccionar una fecha')}
                        onChange={date => {
                          receptionDateRef.current = date;
                        }}
                        maxDate={dayjs()}
                      />
                    </div>
                  ),
                  okText: t('Sí'),
                  okType: 'primary',
                  cancelText: t('No'),
                  onOk() {
                    const pickedDate = receptionDateRef.current;

                    if (!pickedDate) {
                      message.error(t('Debe indicar una fecha de recepción'));

                      return;
                    }

                    void receptionRepository
                      .updateReceptionState(id, PACKAGE_STATES.RECIBIDO, pickedDate)
                      .then(() => {
                        void refetch();
                      });
                  },
                });
              },
              children: t('Marcar como recibido'),
            },
          ]
        : status == PACKAGE_STATES.RECIBIDO && userWorkArea == USER_WORK_AREA.LOGISTICS
          ? [
              {
                icon: <CheckOutlined />,
                onClick: () => {
                  modal.confirm({
                    title: t('¿Estás seguro de que deseas marcar como finalizada esta recepción?'),
                    content: t('Esta acción no se puede deshacer'),
                    okText: t('Sí, finalizar'),
                    okType: 'primary',
                    cancelText: t('No'),
                    onOk() {
                      void receptionRepository
                        .updateReceptionState(id, PACKAGE_STATES.FINALIZADO)
                        .then(() => {
                          void refetch();
                        });
                    },
                  });
                },
                children: 'Finalizar',
                disabled: !canFinishReception,
              },
            ]
          : [];
  }, [
    record,
    status,
    userWorkArea,
    modal,
    receptionRepository,
    id,
    refetch,
    canFinishReception,
    canMarkAsSendingReception,
    t,
  ]);
  const changeStateBlockTooltip = useMemo(
    () =>
      blockChangeState?.length > 0
        ? `${t('No se puede realizar esta acción')}:\n${blockChangeState.map(bs => bs.message).join('\n')}`
        : '',
    [blockChangeState, t],
  );
  const cancelButton = useMemo(
    () =>
      hasPermission('edit') &&
      (status == PACKAGE_STATES.EN_VIAJE || status == PACKAGE_STATES.PENDIENTE) ? (
        <Tooltip title={changeStateBlockTooltip}>
          <Button
            danger
            icon={<CloseOutlined />}
            onClick={() => {
              cancelationMotiveRef.current = '';

              modal.confirm({
                title: t('¿Estás seguro de que deseas cancelar esta recepción?'),

                content: (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {t('Esta acción no se puede deshacer')}
                    <Form.Item layout="vertical" label={t('Motivo de cancelación')} required>
                      <Input
                        placeholder={t('Motivo de cancelación')}
                        onChange={e => {
                          cancelationMotiveRef.current = e.target.value;
                        }}
                      />
                    </Form.Item>
                  </div>
                ),
                okText: t('Sí, cancelar'),
                okType: 'danger',
                cancelText: t('No'),
                onOk() {
                  const cancelationMotive = cancelationMotiveRef.current;

                  if (!cancelationMotive || cancelationMotive.trim() === '') {
                    message.error(t('Debe indicar un motivo de cancelación'));

                    return;
                  }

                  void receptionRepository
                    .updateReception(id, {
                      status: PACKAGE_STATES.CANCELADO,
                      cancellation_motive: cancelationMotive,
                    })
                    .then(() => {
                      void refetch();
                    });
                },
              });
            }}
            disabled={disableStateButtons}
            type={isMediumScreen ? 'text' : 'default'}>
            {t('Cancelar')}
          </Button>
        </Tooltip>
      ) : null,
    [
      hasPermission,
      status,
      changeStateBlockTooltip,
      disableStateButtons,
      isMediumScreen,
      modal,
      receptionRepository,
      id,
      refetch,
      t,
    ],
  );
  const changeStateButton = useMemo(
    () =>
      hasPermission('edit') && changeStateButtonsProps.length > 0
        ? changeStateButtonsProps.map((props: ButtonProps, index) => (
            <Tooltip
              key={index}
              title={
                changeStateBlockTooltip != ''
                  ? changeStateBlockTooltip
                  : record.status == PACKAGE_STATES.PENDIENTE && !canMarkAsSendingReception
                    ? t(
                        'No se puede marcar como enviado hasta que se hayan completado todos los elementos',
                      )
                    : record.status == PACKAGE_STATES.RECIBIDO && !canFinishReception
                      ? t(
                          'No se puede finalizar la recepción hasta que el estado de los items haya sido evaluado y se hayan completado sus números de serie',
                        )
                      : ''
              }>
              <Button
                type={'primary'}
                color={isMediumScreen ? 'primary' : undefined}
                {...props}
                disabled={disableStateButtons || props.disabled}
              />
            </Tooltip>
          ))
        : null,
    [
      t,
      canFinishReception,
      canMarkAsSendingReception,
      changeStateBlockTooltip,
      changeStateButtonsProps,
      disableStateButtons,
      isMediumScreen,
      record.status,
      hasPermission,
    ],
  );
  const popupMenuContent = useMemo(() => {
    const buttons: ReactNode[] = [];

    if (isMediumScreen && changeStateButton) {
      buttons.push(changeStateButton);
    }

    buttons.push(
      <Button
        key={'print'}
        icon={<PrinterOutlined />}
        type="text"
        onClick={() => id && void receptionRepository.printShippingLabel(id)}>
        {t('Imprimir etiqueta')}
      </Button>,
    );

    if (status == PACKAGE_STATES.PENDIENTE) {
      if (tipo != RECEPTION_TYPE.RECEPCION_CREADA_AUTOMATICAMENTE) {
        buttons.push(<ProtectedEditButton key="edit" {...editButtonProps} type="text" />);
      }

      if (isMediumScreen && cancelButton) {
        buttons.push(cancelButton);
      }

      if (tipo != RECEPTION_TYPE.RECEPCION_CREADA_AUTOMATICAMENTE) {
        buttons.push(<ProtectedDeleteButton key="delete" {...deleteButtonProps} type="text" />);
      }
    }

    return buttons;
  }, [
    cancelButton,
    changeStateButton,
    deleteButtonProps,
    editButtonProps,
    id,
    isMediumScreen,
    receptionRepository,
    status,
    t,
    tipo,
  ]);
  const claimButtons = useMemo(
    () =>
      record.active_claim && userWorkArea == USER_WORK_AREA.LOGISTICS ? (
        <>
          <Button
            danger
            onClick={() => {
              // Confirmamos la acción porque al desactivar el reclamo se reinicia el contador de 15 días.
              modal.confirm({
                title: t('¿Estás seguro de que deseas no enviar el reclamo?'),
                content: t('Esta opción reiniciará el contador hasta que vuelvan a pasar 15 días'),
                okText: t('Sí, no enviar'),
                okType: 'primary',
                cancelText: t('Cancelar'),
                onOk() {
                  void receptionRepository.updateReception(id, { active_claim: false }).then(() => {
                    void refetch();
                  });
                },
              });
            }}>
            {t('No reclamar')}
          </Button>
          <Button
            type="primary"
            danger
            icon={<MailOutlined />}
            onClick={() => {
              emailListRef.current = '';
              modal.confirm({
                title: t('¿Estás seguro de que deseas enviar el reclamo?'),
                content: (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <Form.Item layout="vertical" label={t('Emails para reclamo')}>
                      <Select
                        mode="tags"
                        tokenSeparators={[',']}
                        placeholder={t('Ingrese uno o más emails')}
                        onChange={(value: string[]) => {
                          emailListRef.current = emailListToString(value);
                        }}
                      />
                    </Form.Item>
                  </div>
                ),
                okText: t('Enviar'),
                okType: 'primary',
                cancelText: t('Cancelar'),
                onOk() {
                  if (hasInvalidEmails(emailListRef.current)) {
                    message.error(t('Debe ingresar emails válidos'));

                    return;
                  }

                  void receptionRepository
                    .sendClaimEmail(id, emailListRef.current)
                    .then(() => {
                      message.success(t('Reclamo enviado correctamente'));
                      void receptionRepository
                        .updateReception(id, { active_claim: false })
                        .then(() => {
                          void refetch();
                        });
                    })
                    .catch(() => {
                      message.error(t('Error al enviar el reclamo'));
                    });
                },
              });
            }}>
            {t('Reclamar')}
          </Button>
        </>
      ) : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [record.active_claim, userWorkArea],
  );

  return (
    <Flex gap={8} wrap align="center" style={{ minHeight: '40px' }}>
      <RefreshButton
        key="refresh"
        {...refreshButtonProps}
        onClick={() => refetch()}
        hideText
        size={isMediumScreen ? 'small' : 'middle'}
      />

      {!isMediumScreen && (
        <>
          {cancelButton} {claimButtons}
          {changeStateButton}
        </>
      )}

      {popupMenuContent.length > 0 && (
        <PopupMenu
          openButton={<Button icon={<MoreOutlined />} size={isMediumScreen ? 'small' : 'middle'} />}
          tooltip={changeStateBlockTooltip}
          disabled={disableStateButtons}
          menuContent={
            <Flex vertical align="start" gap={8}>
              {popupMenuContent}
            </Flex>
          }
        />
      )}
      {contextHolder}
      <PackageMessages packageId={id} type="reception" />
    </Flex>
  );
};

export default ReceptionShowHeaderButtons;
